import {
  INK, drawBigGrenade, drawCluster, drawCrossbow, drawGrenade, drawHandset, drawHook, drawMassive, drawMeleeWeapon, drawMine, drawPlank,
  drawRocket, drawRubberBall, drawShell, drawSticky, drawTeleporter, drawTennisBall, drawTnt, ink, inkLine, rr, shade,
} from './cartoon';
import { getImage } from './ugcImages';
/**
 * The mole, Raft Wars style: a big round head on a chubby little body, stubby limbs, thick ink
 * outlines and flat colour so hats, scarves and body types read from across the map. Shared by
 * the Locker preview, the lobby diorama and the match renderer.
 * (x, y) is the point between the feet; the mole stands upward from there. Base height ~60px.
 */

export interface MoleLook {
  body: string;
  outfit: string;
  teamColor: string;
  furColor?: string;
  /** Creator uploads (full URLs): drawn in place of the torso / head when loaded. */
  customBody?: string;
  customHead?: string;
}

export type MolePose = 'idle' | 'walk' | 'throw' | 'gun' | 'kick' | 'ragdoll' | 'dead';
/**
 * What a winding-up mole has in its hand: every throw-kind action (the thing itself, or the gadget
 * that calls it in), plus the swung tools. A catapult winder holds nothing: the boulder is in the cup.
 */
export type HeldItem =
  | 'ball' | 'grenade' | 'rpg' | 'mortar' | 'bignade' | 'cluster' | 'mine' | 'grapple' | 'platform' | 'bat' | 'mallet' | 'pike' | 'pickaxe'
  | 'sticky' | 'rubber' | 'airstrike' | 'massive' | 'crossbow' | 'tnt' | 'catapult' | 'teleport' | 'bridge';

export interface MoleDrawOptions {
  facing: 1 | -1;
  scale?: number;
  pose?: MolePose;
  /** Radians, 0 = pointing forward along facing. Used by throw / gun poses. */
  aim?: number;
  walkPhase?: number;
  /** Rotation while tumbling. */
  spin?: number;
  alpha?: number;
  flash?: boolean;
  /** What is in the throwing hand. */
  held?: HeldItem | null;
}

interface BodyShape {
  /** Torso width / height. */
  w: number;
  h: number;
  /** Head radius. */
  head: number;
  legs: number;
  round: number;
  scale: number;
}

const BODY_SHAPES: Record<string, BodyShape> = {
  'body.lean': { w: 26, h: 22, head: 16, legs: 11, round: 0.6, scale: 1 },
  'body.stocky': { w: 34, h: 20, head: 16, legs: 9, round: 0.5, scale: 1 },
  'body.round': { w: 32, h: 26, head: 15, legs: 9, round: 1, scale: 1 },
  'body.lanky': { w: 22, h: 28, head: 14, legs: 16, round: 0.5, scale: 1 },
  'body.tiny': { w: 26, h: 22, head: 16, legs: 11, round: 0.6, scale: 0.72 },
  'body.mega': { w: 30, h: 24, head: 17, legs: 11, round: 0.6, scale: 1.3 },
};

export const DEFAULT_FUR = '#a97a50';

/** Where a creator's body art goes, for the upload hints. */
export const CUSTOM_BODY_BOX = { w: 26, h: 22 };
export const CUSTOM_HEAD_SIZE = 32;

function hand(ctx: CanvasRenderingContext2D, x: number, y: number, fur: string): void {
  ctx.beginPath();
  ctx.arc(x, y, 3.6, 0, Math.PI * 2);
  ink(ctx, fur, 2);
}

function foot(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, flip = 1): void {
  ctx.beginPath();
  ctx.ellipse(x + 2 * flip, y, 8.5, 4.2, 0, 0, Math.PI * 2);
  ink(ctx, color, 2.2);
}

export function drawMole(ctx: CanvasRenderingContext2D, x: number, y: number, look: MoleLook, opts: MoleDrawOptions): void {
  const shape = BODY_SHAPES[look.body] ?? BODY_SHAPES['body.lean']!;
  const s = (opts.scale ?? 1) * shape.scale;
  const fur = look.furColor ?? DEFAULT_FUR;
  const dark = shade(fur, -40);
  const belly = shade(fur, 42);
  const pose = opts.pose ?? 'idle';
  const walk = opts.walkPhase ?? 0;

  ctx.save();
  ctx.translate(x, y);
  if (opts.alpha !== undefined) ctx.globalAlpha = opts.alpha;
  if (pose === 'ragdoll' && opts.spin) {
    // Rotate about the belly so the tumble looks right.
    ctx.translate(0, -shape.legs - shape.h / 2);
    ctx.rotate(opts.spin);
    ctx.translate(0, shape.legs + shape.h / 2);
  }
  if (pose === 'dead') {
    ctx.translate(0, -6 * s);
    ctx.rotate((Math.PI / 2) * -opts.facing);
  }
  ctx.scale(opts.facing * s, s);

  const w = shape.w;
  const h = shape.h;
  const legs = shape.legs;
  const torsoTop = -legs - h;
  const headR = shape.head;
  const headCy = torsoTop - headR * 0.7;
  const bodyImg = look.customBody ? getImage(look.customBody) : null;
  const headImg = look.customHead ? getImage(look.customHead) : null;

  // Legs: stubby, with big round feet
  const swing = pose === 'walk' ? Math.sin(walk) * 5 : 0;
  const kickBack = pose === 'kick' ? 1 : 0;
  const backX = -w * 0.22;
  const frontX = w * 0.22;
  const backEndX = backX - swing - kickBack * 6;
  inkLine(ctx, backX, -legs + 3, backEndX, -3, 6.5, fur);
  foot(ctx, backEndX, -2.5, dark, -1);
  if (pose === 'kick') {
    inkLine(ctx, frontX, -legs + 3, frontX + 24, -legs - 2, 6.5, fur);
    foot(ctx, frontX + 28, -legs - 3, dark);
  } else {
    inkLine(ctx, frontX, -legs + 3, frontX + swing, -3, 6.5, fur);
    foot(ctx, frontX + swing, -2.5, dark);
  }

  // Back arm (behind the torso)
  const shoulderY = torsoTop + h * 0.32;
  const backShoulder = -w * 0.32;
  if (pose === 'throw') {
    inkLine(ctx, backShoulder, shoulderY, backShoulder - 12, shoulderY + 10, 5.5, fur);
    hand(ctx, backShoulder - 12, shoulderY + 10, fur);
  } else if (pose === 'gun') {
    inkLine(ctx, backShoulder, shoulderY, w * 0.1, shoulderY + 9, 5.5, fur);
  } else if (pose === 'ragdoll') {
    inkLine(ctx, backShoulder, shoulderY, backShoulder - 15, shoulderY - 13, 5.5, fur);
    hand(ctx, backShoulder - 15, shoulderY - 13, fur);
  } else {
    const ex = backShoulder - 3 - (pose === 'walk' ? swing * 0.6 : 0);
    inkLine(ctx, backShoulder, shoulderY, ex, shoulderY + h * 0.62, 5.5, fur);
    hand(ctx, ex, shoulderY + h * 0.62, fur);
  }

  if (bodyImg) {
    // A creator's own torso art, fitted to the body box; the team sash stays so sides read at a glance.
    const pad = 3;
    ctx.drawImage(bodyImg, -w / 2 - pad, torsoTop - pad, w + pad * 2, h + pad * 2);
    rr(ctx, -w / 2, torsoTop + h - 5, w, 5, 2.5);
    ink(ctx, look.teamColor, 1.5);
  } else {
    // Torso: an egg with a lighter belly and the team sash across the chest
    rr(ctx, -w / 2, torsoTop, w, h, (Math.min(w, h) / 2) * shape.round);
    ink(ctx, fur);
    ctx.fillStyle = belly;
    ctx.beginPath();
    ctx.ellipse(w * 0.06, torsoTop + h * 0.6, w * 0.3, h * 0.28, 0, 0, Math.PI * 2);
    ctx.fill();
    rr(ctx, -w / 2, torsoTop + h * 0.18, w, 6, 3);
    ink(ctx, look.teamColor, 1.5);

    drawTorsoOutfit(ctx, look.outfit, w, h, torsoTop, look.teamColor);
  }

  if (headImg) {
    // A creator's own head, centred where the drawn one goes; outfits still sit on top.
    const size = headR * 2.6;
    ctx.drawImage(headImg, -size / 2, headCy - size / 2, size, size);
    if (pose === 'dead') deadEyes(ctx, headR, headCy);
    drawHeadOutfit(ctx, look.outfit, headR, headCy, look.teamColor);
  } else {
    // Ear
    ctx.beginPath();
    ctx.arc(-headR * 0.55, headCy - headR * 0.62, headR * 0.34, 0, Math.PI * 2);
    ink(ctx, fur);
    ctx.fillStyle = '#e9a3ab';
    ctx.beginPath();
    ctx.arc(-headR * 0.55, headCy - headR * 0.62, headR * 0.16, 0, Math.PI * 2);
    ctx.fill();
    // Head
    ctx.beginPath();
    ctx.arc(0, headCy, headR, 0, Math.PI * 2);
    ink(ctx, fur);
    // Muzzle
    ctx.beginPath();
    ctx.ellipse(headR * 0.6, headCy + headR * 0.3, headR * 0.62, headR * 0.44, 0, 0, Math.PI * 2);
    ink(ctx, belly, 1.5);
    // Nose
    ctx.beginPath();
    ctx.ellipse(headR * 1.12, headCy + headR * 0.14, 4.2, 3.2, 0, 0, Math.PI * 2);
    ctx.fillStyle = INK;
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.beginPath();
    ctx.arc(headR * 1.0, headCy + headR * 0.06, 1.2, 0, Math.PI * 2);
    ctx.fill();
    // Eye
    if (pose === 'dead') {
      deadEyes(ctx, headR, headCy);
    } else {
      const ex = headR * 0.36;
      const ey = headCy - headR * 0.2;
      ctx.beginPath();
      ctx.ellipse(ex, ey, 5.4, 6.2, 0, 0, Math.PI * 2);
      ink(ctx, '#ffffff', 1.6);
      ctx.fillStyle = INK;
      ctx.beginPath();
      if (pose === 'ragdoll') ctx.arc(ex + 1.5, ey + 1.5, 3.4, 0, Math.PI * 2);
      else ctx.arc(ex + 1.6, ey + 0.6, 2.7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(ex + 2.4, ey - 0.6, 1, 0, Math.PI * 2);
      ctx.fill();
    }
    // Mouth
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    if (opts.flash || pose === 'ragdoll') {
      ctx.ellipse(headR * 0.55, headCy + headR * 0.62, 3, 3.6, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#6b2a2a';
      ctx.fill();
    } else {
      ctx.arc(headR * 0.55, headCy + headR * 0.5, 4.5, 0.35, Math.PI - 0.5);
    }
    ctx.stroke();
    // Whiskers
    ctx.strokeStyle = 'rgba(42,26,18,0.75)';
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    ctx.moveTo(headR * 0.95, headCy + headR * 0.4);
    ctx.lineTo(headR * 1.55, headCy + headR * 0.25);
    ctx.moveTo(headR * 0.95, headCy + headR * 0.52);
    ctx.lineTo(headR * 1.55, headCy + headR * 0.62);
    ctx.stroke();

    drawHeadOutfit(ctx, look.outfit, headR, headCy, look.teamColor);
  }

  // Front arm + what it holds
  const aim = opts.aim ?? 0.6;
  const frontShoulder = w * 0.32;
  if (pose === 'throw') {
    // Wind-up: arm back and up along the aim, hand holds the projectile.
    const hx = frontShoulder + Math.cos(aim) * 19;
    const hy = shoulderY - Math.sin(aim) * 19;
    inkLine(ctx, frontShoulder, shoulderY, hx, hy, 5.5, fur);
    hand(ctx, hx, hy, fur);
    if (opts.held) drawHeld(ctx, opts.held, hx, hy, aim);
  } else if (pose === 'gun') {
    ctx.save();
    ctx.translate(frontShoulder, shoulderY + 2);
    ctx.rotate(-aim);
    inkLine(ctx, 0, 0, 15, 0, 5.5, fur);
    rr(ctx, 7, -3.5, 30, 7, 2.5);
    ink(ctx, '#3a3f47', 2);
    rr(ctx, 5, -2, 10, 10, 2.5);
    ink(ctx, '#8a5a2a', 2);
    hand(ctx, 13, 1, fur);
    ctx.restore();
  } else if (pose === 'ragdoll') {
    inkLine(ctx, frontShoulder, shoulderY, frontShoulder + 14, shoulderY - 14, 5.5, fur);
    hand(ctx, frontShoulder + 14, shoulderY - 14, fur);
  } else if (pose === 'kick') {
    inkLine(ctx, frontShoulder, shoulderY, frontShoulder - 12, shoulderY - 10, 5.5, fur);
    hand(ctx, frontShoulder - 12, shoulderY - 10, fur);
  } else {
    const ex = frontShoulder + 3 + (pose === 'walk' ? swing * 0.6 : 0);
    inkLine(ctx, frontShoulder, shoulderY, ex, shoulderY + h * 0.62, 5.5, fur);
    hand(ctx, ex, shoulderY + h * 0.62, fur);
  }

  if (opts.flash) {
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.fillRect(-w * 1.5, torsoTop - headR * 2.4, w * 3, h + legs + headR * 2.6 + 4);
  }
  ctx.restore();
}

function deadEyes(ctx: CanvasRenderingContext2D, headR: number, headCy: number): void {
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(headR * 0.12, headCy - headR * 0.42);
  ctx.lineTo(headR * 0.6, headCy + headR * 0.02);
  ctx.moveTo(headR * 0.12, headCy + headR * 0.02);
  ctx.lineTo(headR * 0.6, headCy - headR * 0.42);
  ctx.stroke();
}

function drawHeld(ctx: CanvasRenderingContext2D, held: HeldItem, x: number, y: number, aim: number): void {
  switch (held) {
    case 'ball':
      drawTennisBall(ctx, x, y - 2, 6.5, 0);
      break;
    case 'grenade':
      drawGrenade(ctx, x, y - 3, 6, 0);
      break;
    case 'bignade':
      drawBigGrenade(ctx, x, y - 4, 8, 0);
      break;
    case 'cluster':
      drawCluster(ctx, x, y - 3, 7, 0, false);
      break;
    case 'mine':
      drawMine(ctx, x, y - 1, 8, false, 0);
      break;
    case 'grapple':
      ctx.save();
      ctx.translate(x, y - 2);
      ctx.rotate(-aim);
      drawHook(ctx, 0.7);
      ctx.restore();
      break;
    case 'platform':
      drawPlank(ctx, x, y - 3, 36, 6, -aim * 0.5);
      break;
    case 'bat':
    case 'mallet':
    case 'pike':
    case 'pickaxe':
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(-aim);
      drawMeleeWeapon(ctx, held);
      ctx.restore();
      break;
    case 'rpg':
      ctx.save();
      ctx.translate(x, y - 2);
      ctx.rotate(-aim);
      ctx.scale(0.75, 0.75);
      drawRocket(ctx, false, 0);
      ctx.restore();
      break;
    case 'mortar':
      ctx.save();
      ctx.translate(x, y - 2);
      ctx.rotate(-aim);
      ctx.scale(0.7, 0.7);
      drawShell(ctx);
      ctx.restore();
      break;
    case 'sticky':
      drawSticky(ctx, x, y - 3, 6.5, 0.5);
      break;
    case 'rubber':
      drawRubberBall(ctx, x, y - 3, 6.5, 0);
      break;
    // The big bomb is too heavy to hold at arm's length; it is drawn small so the pose still reads.
    case 'massive':
      drawMassive(ctx, x, y - 5, 9, 0, 0.5);
      break;
    case 'tnt':
      drawTnt(ctx, x, y - 4, 0.6, 0);
      break;
    case 'bridge':
      drawPlank(ctx, x, y - 3, 64, 6, -aim * 0.5);
      break;
    case 'airstrike':
      drawHandset(ctx, x, y - 4);
      break;
    case 'teleport':
      ctx.save();
      ctx.translate(x, y - 3);
      drawTeleporter(ctx, 0.5);
      ctx.restore();
      break;
    case 'crossbow':
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(-aim);
      ctx.scale(0.8, 0.8);
      drawCrossbow(ctx);
      ctx.restore();
      break;
    // Empty hands: the boulder sits in the catapult's cup, not in the mole's paw.
    case 'catapult':
      break;
  }
}

function drawTorsoOutfit(ctx: CanvasRenderingContext2D, outfit: string, w: number, h: number, top: number, team: string): void {
  switch (outfit) {
    case 'outfit.scarf':
      rr(ctx, -w / 2 - 2, top - 3, w + 4, 9, 4.5);
      ink(ctx, team, 2);
      rr(ctx, -w / 2 - 7, top + 2, 9, 19, 4);
      ink(ctx, team, 2);
      break;
    case 'outfit.hoodie':
      rr(ctx, -w / 2 - 1, top - 1, w + 2, h + 1, 7);
      ink(ctx, '#4a4f63');
      ctx.fillStyle = '#5e6480';
      rr(ctx, -w * 0.25, top + h * 0.55, w * 0.5, h * 0.3, 3);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-4, top + 4);
      ctx.lineTo(-5, top + 14);
      ctx.moveTo(4, top + 4);
      ctx.lineTo(5, top + 14);
      ctx.stroke();
      rr(ctx, -w / 2 - 1, top + h * 0.18, w + 2, 5, 2.5);
      ink(ctx, team, 1.5);
      break;
    case 'outfit.armor':
      rr(ctx, -w / 2 - 1, top - 1, w + 2, h * 0.75, 5);
      ink(ctx, '#9aa3ad');
      ctx.fillStyle = '#c9d1d9';
      rr(ctx, -w * 0.3, top + 3, w * 0.6, h * 0.18, 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(0, top + h * 0.45, 4.5, 0, Math.PI * 2);
      ink(ctx, team, 1.5);
      break;
    case 'outfit.sharpshooter':
      inkLine(ctx, -w / 2, top + 2, w / 2, top + h - 2, 5, '#c23b5d', 1.2);
      ctx.beginPath();
      ctx.arc(0, top + h / 2, 3.5, 0, Math.PI * 2);
      ink(ctx, '#f7c948', 1.2);
      break;
    case 'outfit.astronaut':
      rr(ctx, -w / 2 - 2, top - 2, w + 4, h + 2, 7);
      ink(ctx, '#dfe9f2');
      rr(ctx, -w * 0.22, top + h * 0.3, w * 0.44, 6, 2);
      ink(ctx, team, 1.5);
      break;
    default:
      break;
  }
}

function drawHeadOutfit(ctx: CanvasRenderingContext2D, outfit: string, r: number, cy: number, team: string): void {
  const top = cy - r;
  switch (outfit) {
    case 'outfit.bandana':
      rr(ctx, -r - 1, top + r * 0.35, r * 2 + 2, 8, 4);
      ink(ctx, '#d43d3d', 2);
      ctx.beginPath();
      ctx.moveTo(-r, top + r * 0.5);
      ctx.lineTo(-r - 13, top + r * 0.95);
      ctx.lineTo(-r - 2, top + r * 0.8);
      ctx.closePath();
      ink(ctx, '#d43d3d', 2);
      break;
    case 'outfit.hardhat':
      ctx.beginPath();
      ctx.ellipse(0, top + 4, r * 1.05, r * 0.82, 0, Math.PI, Math.PI * 2);
      ctx.closePath();
      ink(ctx, '#f2c230', 2.2);
      rr(ctx, -r * 1.3, top + 2, r * 2.6, 5, 2.5);
      ink(ctx, '#f2c230', 2);
      break;
    case 'outfit.goggles':
      rr(ctx, -r, cy - r * 0.45, r * 2, 4, 2);
      ink(ctx, '#2b2b2b', 1.5);
      ctx.beginPath();
      ctx.arc(r * 0.3, cy - r * 0.2, 6, 0, Math.PI * 2);
      ink(ctx, '#7fe3ff', 2.2);
      break;
    case 'outfit.tophat':
      rr(ctx, -r * 1.3, top + 2, r * 2.6, 6, 3);
      ink(ctx, '#1b1b1f', 2);
      rr(ctx, -r * 0.85, top - 20, r * 1.7, 23, 3);
      ink(ctx, '#1b1b1f', 2);
      ctx.fillStyle = team;
      rr(ctx, -r * 0.85, top - 5, r * 1.7, 5, 1);
      ctx.fill();
      break;
    case 'outfit.hoodie':
      ctx.beginPath();
      ctx.arc(0, cy, r + 3, Math.PI * 0.95, Math.PI * 2.05);
      ctx.lineTo(r * 0.7, cy + r * 0.9);
      ctx.lineTo(-r - 3, cy + r * 0.9);
      ctx.closePath();
      ink(ctx, '#4a4f63');
      break;
    case 'outfit.crown':
      ctx.beginPath();
      ctx.moveTo(-r * 0.8, top + 4);
      ctx.lineTo(-r * 0.8, top - 10);
      ctx.lineTo(-r * 0.4, top - 3);
      ctx.lineTo(0, top - 13);
      ctx.lineTo(r * 0.4, top - 3);
      ctx.lineTo(r * 0.8, top - 10);
      ctx.lineTo(r * 0.8, top + 4);
      ctx.closePath();
      ink(ctx, '#f7c948', 2);
      ctx.beginPath();
      ctx.arc(0, top - 1, 2.8, 0, Math.PI * 2);
      ink(ctx, '#e03c5a', 1.2);
      break;
    case 'outfit.astronaut':
      ctx.beginPath();
      ctx.arc(r * 0.15, cy, r * 1.38, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(190, 230, 255, 0.35)';
      ctx.fill();
      ctx.lineWidth = 3.5;
      ctx.strokeStyle = '#dfe9f2';
      ctx.stroke();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = INK;
      ctx.stroke();
      break;
    case 'outfit.headhunter':
      rr(ctx, -r - 1, cy - r * 0.55, r * 2 + 2, 9, 3);
      ink(ctx, '#2b2b2b', 1.5);
      ctx.fillStyle = '#ff3b3b';
      rr(ctx, -r * 0.2, cy - r * 0.45, r * 1.1, 4, 2);
      ctx.fill();
      break;
    case 'outfit.laurels':
      for (let i = -3; i <= 3; i++) {
        ctx.beginPath();
        ctx.ellipse(i * (r / 3.2), top + 3 + Math.abs(i) * 1.6, 4.8, 2.8, i * 0.35, 0, Math.PI * 2);
        ink(ctx, '#4fae5a', 1.3);
      }
      break;
    default:
      break;
  }
}

/** Card art and lobby previews: draws a mole centred in a canvas. */
export function renderMoleCard(canvas: HTMLCanvasElement, look: MoleLook, aim = false, maxScale = 1.1, groundFrac = 1): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const cw = canvas.clientWidth || 150;
  const ch = canvas.clientHeight || 72;
  canvas.width = cw * dpr;
  canvas.height = ch * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, cw, ch);
  const shape = BODY_SHAPES[look.body] ?? BODY_SHAPES['body.lean']!;
  const fullH = (shape.legs + shape.h + shape.head * 2.5) * shape.scale;
  const room = ch * groundFrac - 8;
  const scale = Math.min(maxScale, room / (fullH + 14));
  const groundY = groundFrac >= 1 ? ch - 4 : ch * groundFrac;
  if (groundFrac < 1) {
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(cw / 2, groundY + 2, 22 * scale, 5 * scale, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  drawMole(ctx, cw / 2, groundY, look, { facing: 1, scale, pose: aim ? 'throw' : 'idle', aim: 0.7, held: 'grenade' });
}
